import { calculateTokenPrice } from '@/features/pricing/lib/price'
import type { PricingModel } from '@/features/pricing/types'

import heatFile from './heat.json'
import officialFile from './official-prices.json'

export type PlazaKind = 'text' | 'image' | 'video'

export type PlazaTier = { label: string; price: number }
export type PlazaProtocol = { id: string; label: string }
export type PlazaPerf = { latencyMs: number; tps: number; successRate: number }

export type PlazaModel = {
  name: string
  vendor: string
  icon?: string
  context?: string
  capabilities: string[]
  protocols: PlazaProtocol[]
  kind: PlazaKind
  inputPrice?: number
  cachePrice?: number
  outputPrice?: number
  tiers?: PlazaTier[]
  officialPrice?: number
  discount?: number
  free: boolean
  heat: number
}

type HeatFile = { calls: Record<string, number> }
type OfficialFile = Record<string, { official_input_cny: number }>

const HEAT = (heatFile as unknown as HeatFile).calls ?? {}
const OFFICIAL = officialFile as unknown as OfficialFile

const CAPABILITY_LABELS: Record<string, string> = {
  reasoning: '推理',
  tools: '工具',
  files: '文件',
  vision: '视觉',
  audio: '音频',
  thinking: '思考',
  'open weights': '开源',
  open: '开源',
  weights: '开源',
}

const ENDPOINT_LABELS: Record<string, string> = {
  openai: 'OpenAI 兼容',
  'openai-response': 'Responses',
  'openai-response-compact': 'Responses',
  'openai-video': '视频',
  anthropic: 'Anthropic Messages',
  gemini: 'Gemini',
  embeddings: '向量',
  'jina-rerank': '重排',
  'image-generation': 'OpenAI Images',
}

const IMAGE_TIER_LABELS: Record<string, string> = {
  images_up_to_1_5k: '≤1.5K',
  images_above_1_5k: '>1.5K',
  input_images: '参考图',
  layer_decomposition: '图层拆分',
}

const IMAGE_TAG_HINTS = ['图片', '生图', '图生图', '坐标框', 'image']
const VIDEO_TAG_HINTS = ['视频', 'video']

export function parseTags(tags?: string): string[] {
  return (tags ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
}

export function contextOf(tags: string[]): string | undefined {
  return tags.find((tag) => /^\d+(\.\d+)?[KM]$/.test(tag))
}

export function kindOf(tags: string[]): PlazaKind {
  const lowered = tags.map((tag) => tag.toLowerCase())
  if (lowered.some((tag) => VIDEO_TAG_HINTS.some((hint) => tag.includes(hint)))) {
    return 'video'
  }
  if (lowered.some((tag) => IMAGE_TAG_HINTS.some((hint) => tag.includes(hint)))) {
    return 'image'
  }
  return 'text'
}

// Tier unit prices live in the model's billing expression, e.g.
// u("images_up_to_1_5k") * 0.126 + u("images_above_1_5k") * 0.252
export function tiersOf(model: PricingModel): PlazaTier[] | undefined {
  const expression = model.billing_expr ?? ''
  const tiers: PlazaTier[] = []
  const pattern = /u\("([a-z0-9_]+)"\)\s*\*\s*([0-9]+(?:\.[0-9]+)?)/g
  let match: RegExpExecArray | null
  while ((match = pattern.exec(expression)) !== null) {
    const price = Number(match[2])
    if (!Number.isFinite(price) || price <= 0) continue
    if (match[1] === 'layer_decomposition') continue
    tiers.push({ label: IMAGE_TIER_LABELS[match[1]] ?? match[1], price })
  }
  if (tiers.length > 0) return tiers
  if (model.quota_type === 1 && model.model_price && model.model_price > 0) {
    return [{ label: '单张', price: model.model_price }]
  }
  return undefined
}

export function protocolsOf(
  model: PricingModel,
  kind: PlazaKind
): PlazaProtocol[] {
  if (kind === 'image') {
    return [{ id: 'images', label: 'OpenAI Images' }]
  }
  if (kind === 'video') return [{ id: 'video', label: '视频任务' }]
  return (model.supported_endpoint_types ?? [])
    .filter((type) => type !== 'image-generation')
    .map((type) => ({ id: type, label: ENDPOINT_LABELS[type] ?? type }))
}

export function toPlazaModel(
  model: PricingModel,
  groupRatio: number
): PlazaModel {
  const tags = parseTags(model.tags)
  const kind = kindOf(tags)
  const capabilities = tags
    .filter((tag) => !/^\d+(\.\d+)?[KM]$/.test(tag))
    .map((tag) => CAPABILITY_LABELS[tag.toLowerCase()] ?? tag)
    .filter((label, index, list) => list.indexOf(label) === index)

  const tiers = kind === 'image' ? tiersOf(model) : undefined
  const inputPrice =
    kind === 'image'
      ? undefined
      : calculateTokenPrice(model, 'input', groupRatio) || 0
  const officialPrice = OFFICIAL[model.model_name]?.official_input_cny
  const comparePrice = kind === 'image' ? tiers?.[0]?.price : inputPrice
  const discount =
    officialPrice && comparePrice && comparePrice > 0
      ? (comparePrice / officialPrice) * 10
      : undefined

  return {
    name: model.model_name,
    vendor: model.vendor_name ?? '未知厂商',
    icon: model.icon,
    context: contextOf(tags),
    capabilities,
    protocols: protocolsOf(model, kind),
    kind,
    inputPrice,
    cachePrice:
      kind === 'text'
        ? calculateTokenPrice(model, 'cache', groupRatio) || undefined
        : undefined,
    outputPrice:
      kind === 'text'
        ? calculateTokenPrice(model, 'output', groupRatio) || undefined
        : undefined,
    tiers,
    officialPrice,
    discount,
    free: comparePrice === 0,
    heat: HEAT[model.model_name] ?? 0,
  }
}

export function sortByHeat(models: PlazaModel[]): PlazaModel[] {
  return [...models].sort((a, b) => b.heat - a.heat)
}
